/**
 * Trigonometria, exponencial y logaritmo DETERMINISTAS entre motores.
 *
 * Lo mismo que `len2` hizo con `Math.hypot`, para el resto de la familia. El
 * estandar deja `Math.sin`, `cos`, `atan2`, `acos`, `exp`, `log`, `log1p` y
 * `pow` como "implementation-approximated", y no es teoria: medido el
 * 2026-09-16, con los mismos 20.000 argumentos, Chromium 152 y Node 26 dan bits
 * distintos en `sin`, `cos`, `exp`, `atan2`, `acos`, `log` y `log1p`. Safari
 * usa la libm del sistema y difiere de los dos. En lockstep eso basta para que
 * un partido entre Safari y un Android se separe en cuanto la fisica gira a un
 * jugador o frena el balon.
 *
 * Aqui solo se usan `+ - * /`, `Math.sqrt` y redondeos, que IEEE-754 clava al
 * bit en todas partes, y siempre en el mismo orden. La precision ronda el
 * ulp —sobra para un partido— y lo que importa es que es la MISMA en todas las
 * maquinas.
 *
 * Solo para lo que entra en la simulacion. El dibujo puede seguir usando `Math`.
 */

const PI = 3.141592653589793;
const PI_2 = 1.5707963267948966;
const PI_6 = 0.5235987755982988;
const TWO_OVER_PI = 0.6366197723675814;
/** pi/2 partido en dos: la parte alta tiene 33 bits, asi `n * PIO2_HI` es exacto. */
const PIO2_HI = 1.5707963267341256;
const PIO2_LO = 6.077100506506192e-11;
const LN2_HI = 0.6931471803691238;
const LN2_LO = 1.9082149292705877e-10;
const INV_LN2 = 1.4426950408889634;
const SQRT2 = 1.4142135623730951;
const SQRT1_2 = 0.7071067811865476;
const SQRT3 = 1.7320508075688772;
const TAN_PI_12 = 0.2679491924311227;
const TWO_32 = 4294967296;

/** sin(r) para |r| <= ~pi/4, por Taylor en forma de Horner. */
function sinSmall(r: number): number {
  const r2 = r * r;
  let s = 1;
  for (let k = 19; k >= 3; k -= 2) s = 1 - (r2 / (k * (k - 1))) * s;
  return r * s;
}

/** cos(r) para |r| <= ~pi/4. */
function cosSmall(r: number): number {
  const r2 = r * r;
  let s = 1;
  for (let k = 20; k >= 2; k -= 2) s = 1 - (r2 / (k * (k - 1))) * s;
  return s;
}

/** Cuadrante (0-3) y resto en [-pi/4, pi/4]. */
function reduceAngle(x: number): { q: number; r: number } {
  const n = Math.round(x * TWO_OVER_PI);
  const r = x - n * PIO2_HI - n * PIO2_LO;
  return { q: ((n % 4) + 4) % 4, r };
}

export function dsin(x: number): number {
  if (!Number.isFinite(x)) return NaN;
  const { q, r } = reduceAngle(x);
  if (q === 0) return sinSmall(r);
  if (q === 1) return cosSmall(r);
  if (q === 2) return -sinSmall(r);
  return -cosSmall(r);
}

export function dcos(x: number): number {
  if (!Number.isFinite(x)) return NaN;
  const { q, r } = reduceAngle(x);
  if (q === 0) return cosSmall(r);
  if (q === 1) return -sinSmall(r);
  if (q === 2) return -cosSmall(r);
  return sinSmall(r);
}

export function datan(x: number): number {
  if (x !== x) return NaN;
  const negative = x < 0;
  let a = negative ? -x : x;
  let inverted = false;
  if (a > 1) {
    a = 1 / a;
    inverted = true;
  }
  let offset = 0;
  if (a > TAN_PI_12) {
    // atan(a) = pi/6 + atan((a*sqrt3 - 1) / (sqrt3 + a)): deja |a| <= tan(pi/12).
    a = (a * SQRT3 - 1) / (SQRT3 + a);
    offset = PI_6;
  }
  const a2 = a * a;
  let s = 0;
  for (let k = 18; k >= 0; k--) s = 1 / (2 * k + 1) - a2 * s;
  let result = offset + a * s;
  if (inverted) result = PI_2 - result;
  return negative ? -result : result;
}

export function datan2(y: number, x: number): number {
  if (y !== y || x !== x) return NaN;
  const yNegative = y < 0 || (y === 0 && 1 / y < 0);
  if (x === 0) {
    if (y === 0) return 1 / x < 0 ? (yNegative ? -PI : PI) : y;
    return yNegative ? -PI_2 : PI_2;
  }
  const a = datan(y / x);
  if (x > 0) return a;
  return yNegative ? a - PI : a + PI;
}

export function dacos(x: number): number {
  if (x !== x || x < -1 || x > 1) return NaN;
  return 2 * datan(Math.sqrt((1 - x) / (1 + x)));
}

export function dexp(x: number): number {
  if (x !== x) return NaN;
  if (x > 709.78) return Infinity;
  if (x < -745.2) return 0;
  let k = Math.round(x * INV_LN2);
  const r = x - k * LN2_HI - k * LN2_LO;
  let s = 1;
  for (let n = 18; n >= 1; n--) s = 1 + (r / n) * s;
  // Escalar por 2^k multiplicando por 2 o 1/2: exacto, sin pasar por `pow`.
  while (k > 0) {
    s *= 2;
    k--;
  }
  while (k < 0) {
    s *= 0.5;
    k++;
  }
  return s;
}

/** 2*atanh(s) para |s| <= ~0.2: la serie de la que salen log y log1p. */
function atanhSeries2(s: number): number {
  const s2 = s * s;
  let t = 0;
  for (let k = 16; k >= 0; k--) t = 1 / (2 * k + 1) + s2 * t;
  return 2 * s * t;
}

export function dlog(x: number): number {
  if (x !== x || x < 0) return NaN;
  if (x === 0) return -Infinity;
  if (x === Infinity) return Infinity;
  let e = 0;
  let m = x;
  // Mantisa en [sqrt(1/2), sqrt(2)]. Dividir y multiplicar por potencias de dos
  // es exacto, asi que la descomposicion no introduce ningun error.
  while (m > TWO_32) {
    m /= TWO_32;
    e += 32;
  }
  while (m < 1 / TWO_32) {
    m *= TWO_32;
    e -= 32;
  }
  while (m > SQRT2) {
    m /= 2;
    e++;
  }
  while (m < SQRT1_2) {
    m *= 2;
    e--;
  }
  return e * LN2_HI + (e * LN2_LO + atanhSeries2((m - 1) / (m + 1)));
}

export function dlog1p(x: number): number {
  if (x !== x || x < -1) return NaN;
  if (x === -1) return -Infinity;
  // Cerca de cero, sin pasar por 1 + x: ahi se perderia la precision de x.
  if (x > -0.25 && x < 0.5) return atanhSeries2(x / (2 + x));
  return dlog(1 + x);
}

/** x^y para x >= 0. Los exponentes enteros pequenos, mejor multiplicando a mano. */
export function dpow(x: number, y: number): number {
  if (x !== x || y !== y || x < 0) return NaN;
  if (y === 0) return 1;
  if (x === 0) return y > 0 ? 0 : Infinity;
  return dexp(y * dlog(x));
}
